import type { FlowDefinition, FlowEdge, FlowNode } from '../schema/flow'
import type { AnswerValue, StepAnswers } from '../schema/answers'
import { makeStepKey } from '../ids'
import { getPrimaryField, validateStepAnswers, getRenderBlocks } from './fields'
import { pruneHiddenAnswers } from './visibility'
import type { FieldError } from './fields'
import { resolveNextEdge } from './resolve'
import type { AnswerLookup } from './resolve'
import { computeProgress } from './progress'

/**
 * เครื่องเดินขั้นตอนของ flow
 *
 * ใช้ตัวเดียวกันทั้งใน "โหมดจำลอง" บนหน้า builder และในเว็บแอปจริงของพนักงาน
 * สิ่งที่จำลองได้จึงเท่ากับสิ่งที่เกิดขึ้นจริงเสมอ — ไม่มีทางที่ตรรกะสองชุดจะเพี้ยนจากกัน
 */

/** เพดานกันวนไม่รู้จบใน flow ที่มีเส้นทางย้อนกลับ */
export const MAX_STEPS = 500

export type VisitedStep = {
  nodeId: string
  nodeKey: string
  visitIndex: number
  stepKey: string
  seq: number
}

export type SimulatorSnapshot = {
  currentNodeId: string
  currentVisitIndex: number
  visited: VisitedStep[]
  answersByStepKey: Record<string, StepAnswers>
  finished: boolean
}

export type SubmitResult =
  | { status: 'advanced'; node: FlowNode; edge: FlowEdge; skippedEdgeIds: string[] }
  | { status: 'finished'; node: FlowNode; skippedEdgeIds: string[] }
  | { status: 'invalid'; errors: FieldError[] }
  | { status: 'noMatch' }
  | { status: 'brokenEdge'; edge: FlowEdge }
  | { status: 'tooManySteps' }

export type Simulator = ReturnType<typeof createSimulator>

/**
 * เดินข้ามขั้นตอนที่ "ปิดใช้งาน" ไปเรื่อย ๆ โดยไม่ถามผู้ตอบ — เหมือนขั้นตอนนั้นไม่มีอยู่
 * เดินตามเส้นทางออกของมันเองตามปกติ (ยังใช้เงื่อนไข/ทางสำรองได้ตามที่ตั้งไว้)
 * ถ้าตัดสินใจไม่ได้ (เช่น เงื่อนไขอ้างคำตอบของตัวเองที่ไม่มีทางมีเพราะถูกข้าม และไม่มีทางสำรอง)
 * จะหยุดอยู่ที่ขั้นตอนนั้นแทนที่จะพังทั้งหมด — ผู้ตอบจะเห็นขั้นตอนที่ควรถูกข้ามแทน
 * (checklist ของ builder ควรเตือนกรณีนี้ไว้ล่วงหน้าแล้ว)
 */
function skipDisabledNodes(
  def: FlowDefinition,
  nodeById: Map<string, FlowNode>,
  fromNodeId: string,
  lookup: AnswerLookup,
): { nodeId: string; finished: boolean; edgeIds: string[] } {
  let nodeId = fromNodeId
  const edgeIds: string[] = []
  for (let guard = 0; guard < MAX_STEPS; guard++) {
    const node = nodeById.get(nodeId)
    if (!node?.disabled) return { nodeId, finished: false, edgeIds }

    const result = resolveNextEdge(def, nodeId, lookup)
    if (result.type === 'finish') return { nodeId, finished: true, edgeIds }
    if (result.type !== 'next') return { nodeId, finished: false, edgeIds } // เดินต่อไม่ได้ — หยุดไว้ที่นี่
    edgeIds.push(result.edge.id)
    nodeId = result.targetNode.id
  }
  return { nodeId, finished: false, edgeIds }
}

export function createSimulator(def: FlowDefinition, startNodeId?: string, restore?: SimulatorSnapshot) {
  const nodeById = new Map(def.nodes.map((node) => [node.id, node] as const))

  const initialNodeId = restore?.currentNodeId ?? startNodeId ?? def.startNodeId
  if (!nodeById.has(initialNodeId)) {
    throw new Error(`ไม่พบขั้นตอนเริ่มต้น: ${initialNodeId}`)
  }

  let currentVisitIndex = restore?.currentVisitIndex ?? 0
  let finished = restore?.finished ?? false
  const visited: VisitedStep[] = restore ? [...restore.visited] : []
  const answersByStepKey = new Map<string, StepAnswers>(
    Object.entries(restore?.answersByStepKey ?? {}),
  )
  let progressValue = 0

  const requireNode = (nodeId: string): FlowNode => {
    const node = nodeById.get(nodeId)
    if (!node) throw new Error(`ไม่พบขั้นตอน: ${nodeId}`)
    return node
  }

  /**
   * อ่านคำตอบสำหรับประเมินเงื่อนไข — คิดจาก "เส้นทางที่เดินมาแล้วเท่านั้น"
   * ขั้นตอนที่ถูกย้อนกลับออกไปจะไม่ถูกนับ (แต่ค่ายังถูกเก็บไว้เติมกลับให้อัตโนมัติ)
   */
  const lookup: AnswerLookup = (nodeKey, fieldName) => {
    let found: AnswerValue | undefined
    for (const step of visited) {
      if (step.nodeKey !== nodeKey) continue
      const answers = answersByStepKey.get(step.stepKey)
      if (!answers) continue
      const key = fieldName ?? getPrimaryField(requireNode(step.nodeId))?.name
      if (!key) continue
      if (key in answers) found = answers[key]
    }
    return found
  }

  // ขั้นตอนเริ่มต้น (หรือจุดกู้คืน) อาจถูกปิดใช้งานไปแล้วก็ได้ — ข้ามให้เรียบร้อยตั้งแต่แรก
  const initialSkip = skipDisabledNodes(def, nodeById, initialNodeId, lookup)
  let currentNodeId = initialSkip.nodeId
  if (initialSkip.finished) finished = true

  const currentStepKey = (): string => makeStepKey(currentNodeId, currentVisitIndex)

  return {
    definition: def,

    current(): FlowNode {
      return requireNode(currentNodeId)
    },

    currentStepKey,

    isFinished(): boolean {
      return finished
    },

    /** คำตอบเดิมของขั้นตอนปัจจุบัน (ใช้เติมกลับเมื่อกดย้อนกลับ) */
    currentAnswers(): StepAnswers {
      return answersByStepKey.get(currentStepKey()) ?? {}
    },

    answersFor(stepKey: string): StepAnswers {
      return answersByStepKey.get(stepKey) ?? {}
    },

    path(): readonly VisitedStep[] {
      return visited
    },

    /** id ของเส้นทางที่เดินผ่านมาแล้ว — ใช้ไฮไลต์บนกราฟในโหมดจำลอง */
    traversedNodeIds(): string[] {
      return [...visited.map((step) => step.nodeId), currentNodeId]
    },

    lookup,

    canGoBack(): boolean {
      return visited.length > 0
    },

    progress(): number {
      if (finished) return 1
      progressValue = computeProgress(def, currentNodeId, visited.length, progressValue)
      return progressValue
    },

    submit(answers: StepAnswers): SubmitResult {
      if (finished) return { status: 'tooManySteps' }
      if (visited.length >= MAX_STEPS) return { status: 'tooManySteps' }

      const node = requireNode(currentNodeId)
      const validation = validateStepAnswers(node, answers)
      if (!validation.ok) return { status: 'invalid', errors: validation.errors }

      // ทิ้งคำตอบของช่องที่ถูกซ่อนอยู่ตอนกดถัดไป — เช่นพิมพ์ "อื่น ๆ" ไว้แล้วเปลี่ยนใจไปเลือกตัวเลือกอื่น
      // ข้อความเดิมต้องไม่ติดไปกับข้อมูลจริง และต้องไม่ไปมีผลกับเงื่อนไขเส้นทางด้วย
      const cleaned = pruneHiddenAnswers(getRenderBlocks(node), answers)

      const stepKey = currentStepKey()
      answersByStepKey.set(stepKey, cleaned)
      visited.push({
        nodeId: currentNodeId,
        nodeKey: node.key,
        visitIndex: currentVisitIndex,
        stepKey,
        seq: visited.length,
      })

      const result = resolveNextEdge(def, currentNodeId, lookup)
      if (result.type === 'finish') {
        finished = true
        return { status: 'finished', node, skippedEdgeIds: [] }
      }
      if (result.type === 'noMatch') {
        visited.pop()
        return { status: 'noMatch' }
      }
      if (result.type === 'brokenEdge') {
        visited.pop()
        return { status: 'brokenEdge', edge: result.edge }
      }

      const skip = skipDisabledNodes(def, nodeById, result.targetNode.id, lookup)
      currentNodeId = skip.nodeId
      currentVisitIndex = visited.filter((step) => step.nodeId === currentNodeId).length

      if (skip.finished) {
        finished = true
        return { status: 'finished', node: requireNode(currentNodeId), skippedEdgeIds: skip.edgeIds }
      }
      return {
        status: 'advanced',
        node: requireNode(currentNodeId),
        edge: result.edge,
        skippedEdgeIds: skip.edgeIds,
      }
    },

    /** ปิดงาน (ใช้เมื่อผู้ตอบกดยืนยันบนหน้าจบ) */
    finish(): void {
      finished = true
    },

    back(): boolean {
      if (visited.length === 0) return false
      const previous = visited.pop() as VisitedStep
      currentNodeId = previous.nodeId
      currentVisitIndex = previous.visitIndex
      finished = false
      progressValue = 0 // ให้แถบความคืบหน้าคำนวณใหม่ตามจริงเมื่อย้อนกลับ
      return true
    },

    /**
     * กระโดดไปยังขั้นตอนใดก็ได้ทันที โดย "ไม่ล้างคำตอบที่ตอบไปแล้ว"
     * ใช้ในโหมดจำลอง — เลือกขั้นตอนใหม่แล้วทดสอบต่อได้เลย ไม่ต้องเริ่มใหม่ตั้งแต่ต้น
     *
     * ถ้าเคยไปขั้นตอนนี้มาก่อน (revisit/ย้อนกลับไปจุดเดิม) จะตัดประวัติหลังจากครั้งล่าสุด
     * ที่ไปทิ้ง — เหมือนกดย้อนกลับมาจนถึงจุดนั้น เพื่อไม่ให้มีคำตอบ "อนาคต" ที่ไม่สอดคล้อง
     * กับเส้นทางใหม่ค้างอยู่ ส่วนขั้นตอนที่ยังไม่เคยไปจะกระโดดตรงไปโดยคงเส้นทางเดิมไว้ทั้งหมด
     */
    jumpTo(nodeId: string): boolean {
      if (!nodeById.has(nodeId)) return false

      if (nodeId !== currentNodeId) {
        const lastIndex = visited.reduce(
          (found, step, index) => (step.nodeId === nodeId ? index : found),
          -1,
        )
        if (lastIndex >= 0) visited.length = lastIndex

        currentNodeId = nodeId
        currentVisitIndex = visited.filter((step) => step.nodeId === nodeId).length
      }

      finished = false
      progressValue = 0 // คำนวณความคืบหน้าใหม่จากจุดที่กระโดดมาตามจริง

      // ถ้ากระโดดไปเจอขั้นตอนที่ปิดใช้งานพอดี ให้ข้ามต่อทันที — ผู้ตอบจริงไม่มีทางเห็นขั้นตอนนี้เลย
      const skip = skipDisabledNodes(def, nodeById, currentNodeId, lookup)
      currentNodeId = skip.nodeId
      currentVisitIndex = visited.filter((step) => step.nodeId === currentNodeId).length
      if (skip.finished) finished = true

      return true
    },

    snapshot(): SimulatorSnapshot {
      return {
        currentNodeId,
        currentVisitIndex,
        visited: visited.map((step) => ({ ...step })),
        answersByStepKey: Object.fromEntries(answersByStepKey),
        finished,
      }
    },
  }
}
